import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '@/common/SafeIcon';

const { FiZap, FiUser, FiLock, FiArrowRight, FiAlertCircle, FiShield, FiEye, FiEyeOff } = FiIcons;

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      const res = await login(username, password);
      if (res.success) {
        navigate('/');
      } else {
        setError(res.error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-surface-50">
      {/* Left decorative panel */}
      <div className="hidden lg:flex flex-col justify-between w-5/12 bg-surface-900 p-12 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-full h-full opacity-5" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px,#6366f1 1px,transparent 0)', backgroundSize: '32px 32px' }} />
          <div className="absolute -top-32 -right-32 w-96 h-96 bg-primary-600/20 rounded-full blur-3xl" />
          <div className="absolute -bottom-32 -left-32 w-96 h-96 bg-accent-500/10 rounded-full blur-3xl" />
        </div>
        <div className="flex items-center space-x-3 relative z-10">
          <div className="w-10 h-10 rounded-xl bg-primary-500 flex items-center justify-center">
            <SafeIcon icon={FiZap} className="w-5 h-5 text-white" />
          </div>
          <span className="text-white font-bold text-xl tracking-tight">RewardsAdmin</span>
        </div>
        <div className="relative z-10 space-y-8">
          <div className="space-y-4">
            <div className="inline-flex items-center space-x-2 bg-primary-500/15 border border-primary-500/25 rounded-full px-4 py-1.5">
              <SafeIcon icon={FiShield} className="w-3.5 h-3.5 text-primary-400" />
              <span className="text-primary-300 text-sm font-medium">Secure Admin Portal</span>
            </div>
            <h2 className="text-4xl font-bold text-white leading-tight">
              Campaign<br />
              <span className="text-primary-400">Management</span> Platform
            </h2>
            <p className="text-surface-400 text-base leading-relaxed max-w-xs">
              Monitor analytics, manage users, and track your rewards campaign in real-time.
            </p>
          </div>
          <div className="grid grid-cols-3 gap-4">
            {[['Upload', 'New Users'], ['View', 'All Users'], ['Export', 'Filtered Users']].map(([val, label]) => (
              <div key={label} className="bg-surface-800 rounded-xl p-4 border border-surface-700">
                <p className="text-2xl font-bold text-white">{val}</p>
                <p className="text-surface-400 text-xs mt-1">{label}</p>
              </div>
            ))}
          </div>
        </div>
        <p className="text-surface-600 text-xs relative z-10">© 2026 CISPL Gifts Portal. All rights reserved.</p>
      </div>

      {/* Right form panel */}
      <div className="flex flex-1 items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center justify-center space-x-2 mb-10">
            <div className="w-9 h-9 rounded-xl bg-primary-600 flex items-center justify-center">
              <SafeIcon icon={FiZap} className="w-5 h-5 text-white" />
            </div>
            <span className="text-surface-900 font-bold text-xl">RewardsAdmin</span>
          </div>
          <div className="mb-8">
            <h3 className="text-2xl font-bold text-surface-900">Welcome back</h3>
            <p className="text-surface-500 text-sm mt-1.5">Sign in to your admin account to continue.</p>
          </div>
          <div className="bg-white rounded-2xl border border-surface-200 shadow-card p-6 space-y-5">
            {error && (
              <div className="flex items-start space-x-3 rounded-xl bg-red-50 border border-red-200 p-3.5">
                <SafeIcon icon={FiAlertCircle} className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-1.5">
                <label className="text-sm font-semibold text-surface-700">Username</label>
                <div className="relative">
                  <SafeIcon icon={FiUser} className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-surface-400" />
                  <Input 
                    type="text" 
                    placeholder="Enter username" 
                    className="pl-10" 
                    value={username} 
                    onChange={(e) => setUsername(e.target.value)} 
                    required 
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-semibold text-surface-700">Password</label>
                <div className="relative group">
                  <SafeIcon icon={FiLock} className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-surface-400" />
                  <Input 
                    type={showPassword ? "text" : "password"} 
                    placeholder="Enter password" 
                    className="pl-10 pr-10" 
                    value={password} 
                    onChange={(e) => setPassword(e.target.value)} 
                    required 
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-surface-400 hover:text-primary-500 transition-colors focus:outline-none"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    <SafeIcon icon={showPassword ? FiEyeOff : FiEye} className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <Button type="submit" className="w-full h-11 text-sm font-semibold flex items-center justify-center space-x-2 mt-1" disabled={isLoading}>
                {isLoading ? (
                  <span className="animate-pulse">Signing in...</span>
                ) : (
                  <>
                    <span>Sign In</span>
                    <SafeIcon icon={FiArrowRight} className="w-4 h-4" />
                  </>
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;